import { MRT_ColumnDef } from 'mantine-react-table';
import { Text, TextInput } from '@mantine/core';
import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import { formatPriceWithoutCurrency } from 'app/utils/util-functions';
import { columnDefSubTradeAllocation } from 'app/models/sub-trade-params';
import { useDispatch, useSelector } from 'react-redux';
import { addTemporaryTradedFacility, updateTradedFacilityAmount } from 'app/state/ducks/traded-facilities/actions';
import { getTradedFacilities } from 'app/state/ducks/traded-facilities/selectors';
import { updateSubTradeClientId } from 'app/state/ducks/sub-trades/actions';

const getTextStyle = (color: string, weight = 600): CSSProperties => ({
    fontFamily: 'Plus Jakarta Sans',
    fontSize: '14px',
    fontWeight: weight,
    lineHeight: '19px',
    textAlign: 'left',
    color,
});

function TableHead({ header }: { header: string }) {
    return (
        <Text style={getTextStyle('#784595', 400)}>
            {header}
        </Text>
    );
}

export const columnDefsAllocation: MRT_ColumnDef<columnDefSubTradeAllocation>[] = [
    {
        id: 'subTradeId',
        size: 50,
        accessorKey: 'subTradeId',
        header: 'Subtrade ID',
        Header: <TableHead header={'SubTrade ID'} />,
        Cell: ({ row }) => {
            const status = row.original.status;
            return row.original.entityName ? (
                <Text style={{ ...getTextStyle('#476DB9') }}>
                    {row.original.subTradeId} {status !== 'Settled' ? '' : `(${status})`}
                </Text>
            ) : <></>

        },
    },
    {
        size: 150,
        accessorKey: 'clientId',
        header: 'Internal SubTrade ID',
        Header: <TableHead header={'Internal SubTrade ID'} />,
        Cell: ({ row }) => {
            const status = row.original.status;
            const isEntityRow = row.original.entityName != "";
            const value = row.original.clientId
            const dispatch = useDispatch();
            const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                const newValue = e.target.value
                if (row.original.onClientIdChange) {
                    row.original.onClientIdChange(row.index, newValue);
                    dispatch(updateSubTradeClientId({ id: row.original.contractId?.id, clientId: newValue }));
                }
            };
            return isEntityRow ? (
                <div className={'allocation-column-def-amount'}>
                    <TextInput
                        disabled={status === 'Settled'}
                        value={value}
                        onChange={handleChange}
                        variant="unstyled"
                        aria-label="Internal SubTrade ID"
                        classNames={{ input: 'allocation-column-def-amount-text' }}
                    />
                </div>
            ) : <></>
        },
    },
    {
        id: 'entityName',
        accessorKey: 'entityName',
        size: 250,
        header: 'Entity Name',
        Header: <TableHead header={'Entity Name'} />,
        Cell: ({ row }) => {
            return (
                <Text style={{ ...getTextStyle('#476DB9') }}>
                    {row.original.entityName ?? 'N/A'}
                </Text>
            );
        },
    },

    {
        size: 50,
        accessorKey: 'mei',
        header: 'MEI',
        Header: <TableHead header={'MEI'} />,
        Cell: ({ row }) => {
            return (
                <Text style={{ ...getTextStyle('#476DB9') }}>
                    {row.original.mei}
                </Text>
            );
        },
    },

    {
        size: 250,
        accessorKey: 'facilityName',
        header: 'Facility Name',
        Header: <TableHead header={'Facility Name'} />,
        Cell: ({ row }) => {
            return (
                <Text style={{ ...getTextStyle('#476DB9') }}>
                    {row.original.facilityName}
                </Text>
            );
        },
    },

    {
        size: 150,
        accessorKey: 'amount',
        header: 'Amount',
        Header: <TableHead header={'Amount'} />,
        Cell: ({ row }) => {
            // Initialize with a formatted value
            const initialFormatted = formatPriceWithoutCurrency(Number(row.original.amount), 2, 2);
            const status = row.original.status;
            const isOverallocated = row.original.isOverallocated ?? false;
            const dispatch = useDispatch();
            const [rawValue, setRawValue] = useState(initialFormatted);
            const allTradedFacilities = useSelector(getTradedFacilities)
            const [isFocused, setIsFocused] = useState(false);

            // Bug Fix 1: Track last known amount to prevent aggressive overwrites
            const [lastKnownAmount, setLastKnownAmount] = useState(Number(row.original.amount));

            // Update cell value if underlying amount changes and we're not editing
            useEffect(() => {
                const currentAmount = Number(row.original.amount);
                if (!isFocused && currentAmount !== lastKnownAmount) {
                    setRawValue(formatPriceWithoutCurrency(currentAmount, 2, 2));
                    setLastKnownAmount(currentAmount);
                }
            }, [row.original.amount, isFocused, lastKnownAmount]);

            const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                // While editing, store a plain numeric string (or raw value)
                setRawValue(e.target.value);
                const newValue = Number(e.target.value.replace(/[^0-9.-]+/g, ''));
                if (row.original.onAmountChange && !isNaN(newValue)) {
                    row.original.onAmountChange(row.index, newValue);
                    const index = allTradedFacilities.findIndex(
                        (tf) =>
                            tf.facilityId?.id === row.original.facilityWithAmount?.facilityId?.id &&
                            tf.contractId?.id === row.original.contractId?.id,
                    )
                    if (index >= 0) {
                        dispatch(updateTradedFacilityAmount({ tradeAmount: newValue, index }))
                    } else if (row.original.contractId?.id && row.original.facilityWithAmount?.facilityId) {
                        dispatch(addTemporaryTradedFacility({
                            facilityId: row.original.facilityWithAmount.facilityId,
                            tradeAmount: newValue,
                            contractType: 'SubTradeFacility',
                            subTradeId: row.original.contractId.id,
                        }))
                    }
                }
            };

            const handleFocus = () => {
                setIsFocused(true);
                // Remove formatting when focusing, so the user can edit the raw number.
                const plain = rawValue.replace(/[^0-9.-]+/g, '');
                setRawValue(plain);
            };

            const handleBlur = () => {
                setIsFocused(false);
                const numeric = Number(rawValue);
                if (!isNaN(numeric)) {
                    // Clamp an over-allocated amount down to the facility's remaining
                    // balance (the parent shows the error toast and syncs state/Redux).
                    const finalValue = row.original.onAmountBlur
                        ? row.original.onAmountBlur(row.index, numeric)
                        : numeric;
                    setRawValue(formatPriceWithoutCurrency(Number(finalValue), 2, 2));
                    setLastKnownAmount(finalValue);
                }
            };

            return (
                <div className={'allocation-column-def-amount playwright-subtrade-amount-class'}>
                    <TextInput
                        disabled={status === 'Settled'}
                        value={rawValue}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        variant="unstyled"
                        classNames={{ input: 'allocation-column-def-amount-text playwright-subtrade-amount-class-text' }}
                        styles={{
                            input: isOverallocated ? { color: '#EE5959 !important' } : undefined
                        }}
                    />
                </div>
            );
        },
    },
    {
        size: 80,
        accessorKey: 'currency',
        header: 'Currency',
        Header: <TableHead header={'Currency'} />,
        Cell: ({ row }) => {
            return (
                <Text style={{ ...getTextStyle('#476DB9') }}>
                    {row.original.currency}
                </Text>
            );
        },
    },
    {
        id: 'delete',
        size: 30,
        header: '',
        Header: () => (<TableHead header={''} />
        ),
        Cell: ({ row }) => {
            const status = row.original.status;
            return row.original.entityName && status !== "Settled" ? (
                <Text
                    style={{ ...getTextStyle('#EE5959'), cursor: 'pointer' }}
                    onClick={() => {
                        if (row.original.onDelete) {
                            row.original.onDelete(row.index, row.original);
                        }
                    }}
                >
                    Delete
                </Text>
            ) : null;
        },
    }
];
